/**
 * useChangePasswordForm — manages change-password form state.
 *
 * State machine: idle → submitting → success | error
 * On success: success=true, show message, start 2s timer, call onSuccess()
 * On error: error=string, stay on form
 *
 * @param onSuccess — called after 2s success timer completes
 * @param onSubmit — called with (oldPassword, newPassword) on valid submit
 */
import { useState, useEffect, type FormEvent } from "react";
import { usePasswordStrength } from "@/shared/lib/design-tokens";
import { toast } from "@/shared/lib/toast";

interface UseChangePasswordFormOptions {
  onSuccess: () => void;
  onSubmit: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string
  ) => Promise<void>;
  /** Localized confirmation shown as a global success toast on completion. */
  successMessage: string;
}

interface UseChangePasswordFormReturn {
  // Field values
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  // Show/hide toggles
  showOldPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  // Submission state
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
  // Strength helpers
  strength: ReturnType<typeof usePasswordStrength>;
  passwordsMatch: boolean;
  allRequirementsMet: boolean;
  canSubmit: boolean;
  // Setters
  setOldPassword: (v: string) => void;
  setNewPassword: (v: string) => void;
  setConfirmPassword: (v: string) => void;
  setShowOldPassword: (v: boolean) => void;
  setShowNewPassword: (v: boolean) => void;
  setShowConfirmPassword: (v: boolean) => void;
  // Submit handler
  handleFormSubmit: (e: FormEvent) => void;
  // State setters for use by component
  setSubmitError: (err: string | null) => void;
  setSubmitSuccess: (val: boolean) => void;
}

export function useChangePasswordForm({
  onSuccess,
  onSubmit,
  successMessage,
}: UseChangePasswordFormOptions): UseChangePasswordFormReturn {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const strength = usePasswordStrength(newPassword);
  const allRequirementsMet = strength.score >= 4;
  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword.length > 0;
  const canSubmit =
    oldPassword.length > 0 && allRequirementsMet && passwordsMatch && !isSubmitting;

  // 2-second success timer
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      onSuccess();
    }, 2000);
    return () => clearTimeout(timer);
  }, [success, onSuccess]);

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      await onSubmit(oldPassword, newPassword, confirmPassword);
      setSuccess(true);
      toast.success(successMessage);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    oldPassword,
    newPassword,
    confirmPassword,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
    isSubmitting,
    error,
    success,
    strength,
    passwordsMatch,
    allRequirementsMet,
    canSubmit,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setShowOldPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    handleFormSubmit,
    setSubmitError: setError,
    setSubmitSuccess: setSuccess,
  };
}